/**
 * Fleet Invoice Management API
 *
 * Fleet-portal (CLIENT user) invoice endpoints.
 *
 * SCOPING: no call sends a customer id. The backend resolves the fleet customer
 * from the `customerUuid` JWT claim and scopes every read to it — and for the
 * PDF it verifies the invoice actually belongs to the caller before returning
 * anything. An invoice UUID on its own is not authorisation.
 */

import axiosInstance from './axiosInstance';

// ============================================
// List
// ============================================

/**
 * List the fleet customer's invoices, newest first, optionally within a date window.
 *
 * `from` / `to` are INCLUSIVE ISO dates (YYYY-MM-DD) matched against the invoice's
 * ISSUE date — the date printed on the bill, which is the one a customer can
 * reconcile against their own records. They are independently optional: omit both
 * for all time (what the desktop page does), or send a window for the phone app's
 * 7-day default and custom ranges.
 *
 * Each param is omitted from the query string entirely when absent rather than sent
 * as an empty string — Micronaut binds `from=` to a parse failure, not to null, so
 * an empty value 400s the whole request.
 *
 * DRAFT and VOID invoices are never returned; the server excludes them.
 *
 * @param {{ page?: number, size?: number, from?: string, to?: string }} params
 * @returns {Promise<{ content: Array, page: number, size: number, totalElements: number, totalPages: number }>}
 *   Each row: { uuid, invoiceNumber, status, issueDate, dueDate, totalAmount,
 *               amountPaid, balanceDue, bookingCode, vehicleRego,
 *               vehicleDisplayName, hasPdf, paid }
 */
export const listFleetInvoicesApi = async ({ page = 0, size = 20, from, to } = {}) => {
    const params = { page, size };
    if (from) params.from = from;
    if (to) params.to = to;

    const response = await axiosInstance.get('/fleet/invoices', { params });
    return response.data;
};

// ============================================
// PDF
// ============================================

/**
 * Download the PDF for one of the fleet customer's invoices.
 *
 * Only available once the invoice has been issued — a DRAFT has no PDF yet, so
 * rows carry a `hasPdf` flag and the UI only offers a download when it's true.
 *
 * Returns a Blob; the caller is responsible for triggering the browser download.
 */
export const downloadFleetInvoicePdfApi = async (invoiceUuid) => {
    const response = await axiosInstance.get(`/fleet/invoices/${invoiceUuid}/pdf`, {
        responseType: 'blob',
    });
    return response.data;
};

// ============================================
// Aggregate export
// ============================================

const fleetInvoiceMgtApi = {
    listFleetInvoicesApi,
    downloadFleetInvoicePdfApi,
};

export default fleetInvoiceMgtApi;